<script lang="ts">
  import { onMount } from 'svelte';
  import Button from './Button.svelte';
  import { accountDevices, accountError, changeAccountDevice, signOut, type AccountDevice } from './api';
  import { gate } from './session.svelte';

  let devices = $state<AccountDevice[]>([]);
  let loading = $state(true);
  let busy = $state('');
  let error = $state('');
  let notice = $state('');
  let now = $state(Date.now());

  onMount(() => {
    void load();
    const timer = setInterval(() => { now = Date.now(); }, 30_000);
    return () => clearInterval(timer);
  });

  async function load(): Promise<void> {
    loading = true;
    error = '';
    try {
      devices = (await accountDevices()).devices;
      now = Date.now();
    } catch (err) {
      error = accountError(err);
    } finally {
      loading = false;
    }
  }

  function date(value: number | string): string {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : 'Date unavailable';
  }

  function removable(device: AccountDevice): boolean {
    // The server supplies the exact six-calendar-month boundary.
    const boundary = new Date(device.lockedUntil).getTime();
    return Number.isFinite(boundary) && now >= boundary;
  }

  async function change(device: AccountDevice, action: 'revoke' | 'remove'): Promise<void> {
    if (busy || loading || (action === 'remove' && !removable(device))) return;
    const message = action === 'revoke'
      ? `Revoke access for "${device.label || 'Unnamed device'}"? Its sessions will end, but its device slot stays reserved until you remove it.`
      : `Remove "${device.label || 'Unnamed device'}" and free its device slot? Its sessions will end.`;
    if (!window.confirm(message + (device.current ? ' This will sign you out of this browser.' : ''))) return;
    busy = device.id;
    error = '';
    notice = '';
    try {
      await changeAccountDevice(device.id, action);
      if (device.current) {
        location.reload();
        return;
      }
      notice = action === 'revoke' ? 'Device access revoked.' : 'Device removed.';
      await load();
    } catch (err) {
      error = accountError(err);
    } finally {
      busy = '';
    }
  }

  async function logout(): Promise<void> {
    if (busy) return;
    busy = 'logout';
    error = '';
    try {
      await signOut();
      // Leave the HttpOnly device cookie alone so this browser keeps its slot.
      location.reload();
    } catch (err) {
      error = accountError(err);
    } finally {
      busy = '';
    }
  }
</script>

<section class="account" aria-labelledby="account-heading">
  <header>
    <div>
      <h2 id="account-heading">Account and devices</h2>
      {#if gate.account}<p class="email">{gate.account.email}</p>{/if}
    </div>
    <Button disabled={!!busy} loading={busy === 'logout'} onclick={logout}>Sign out</Button>
  </header>
  <p class="hint">Each device slot is reserved for six calendar months from registration. Revoke access at any time; remove a device after its lock expires to free the slot.</p>
  <p class="hint">A device is a browser profile. Signing out keeps it registered. Clearing cookies does not free its slot and may make this browser count as a new device.</p>
  {#if error}<p class="error" role="alert">{error}</p>{/if}
  {#if notice}<p class="notice" role="status">{notice}</p>{/if}
  {#if loading}
    <p class="hint" role="status">Loading devices…</p>
  {:else}
    <ul>
      {#each devices as device (device.id)}
        <li>
          <div class="device-title"><h3>{device.label || 'Unnamed device'}</h3>{#if device.current}<span>This browser</span>{/if}</div>
          <p class="hint">Registered {date(device.registeredAt)}</p>
          {#if device.revokedAt !== null}<p class="hint">Access revoked {date(device.revokedAt)}</p>{/if}
          <p class="hint">{removable(device) ? 'Removal available since' : 'Removal available on'} {date(device.lockedUntil)}</p>
          <div class="actions">
            {#if device.revokedAt === null}
              <Button disabled={!!busy} loading={busy === device.id} onclick={() => change(device, 'revoke')}>Revoke access</Button>
            {/if}
            <Button disabled={!!busy || !removable(device)} title={removable(device) ? 'Remove this device and free its slot' : `Available after ${date(device.lockedUntil)}`} onclick={() => change(device, 'remove')}>Remove device</Button>
          </div>
        </li>
      {:else}
        {#if !error}<li class="hint">No registered devices.</li>{/if}
      {/each}
    </ul>
  {/if}
  <Button variant="ghost" disabled={loading || !!busy} onclick={load}>Refresh devices</Button>
</section>

<style>
  .account { margin-top: var(--space-5); padding: var(--space-4); border-radius: var(--radius-lg); border: 1px solid var(--glass-line); background: var(--glass-fill); }
  header, .device-title, .actions { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
  header { justify-content: space-between; margin-bottom: var(--space-3); }
  header > div { min-width: 0; }
  h2 { font-size: var(--text-sm); font-weight: var(--weight-semi); }
  h3 { font-size: var(--text-sm); font-weight: var(--weight-semi); overflow-wrap: anywhere; }
  .email { color: var(--text-dim); font-size: var(--text-sm); overflow-wrap: anywhere; }
  .hint { font-size: var(--text-xs); color: var(--text-dim); line-height: var(--leading); margin-top: var(--space-2); }
  ul { list-style: none; padding: 0; margin: var(--space-3) 0; }
  li { border-top: 1px solid var(--line); padding-block: var(--space-3); }
  .device-title span { color: var(--text-dim); font-size: var(--text-xs); }
  .actions { margin-top: var(--space-3); }
  .error, .notice { margin-top: var(--space-3); font-size: var(--text-sm); }
  .error { color: var(--bad); }
  .notice { color: var(--good); }
</style>
